import React, { useState, useEffect, useRef } from 'react';
import { Bell, X, Check, Clock, AlertCircle, Car } from 'lucide-react';
import { notificationsAPI, bookingsAPI } from '../services/api';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import DriverRequestsModal from './DriverRequestsModal';
import { useTranslation } from 'react-i18next';

interface Notification {
  _id: string;
  type: string;
  title: string;
  message: string;
  isRead: boolean;
  createdAt: string;
  bookingId?: any;
}

const GeneralNotificationBell: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [unreadCount, setUnreadCount] = useState(0);
  const [driverRequestsCount, setDriverRequestsCount] = useState(0);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isDriverRequestsModalOpen, setIsDriverRequestsModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  
  // Check if user is driver or admin
  const isDriver = user?.role === 'provider' && user?.skills?.includes('driver');
  const isAdmin = user?.role === 'admin';
  const canSeeDriverRequests = isDriver || isAdmin;

  useEffect(() => {
    fetchUnreadCount();
    if (canSeeDriverRequests) {
      fetchDriverRequestsCount();
    }
    
    // Poll every 10 seconds
    const interval = setInterval(() => {
      fetchUnreadCount();
      if (canSeeDriverRequests) {
        fetchDriverRequestsCount();
      }
      if (isOpen) {
        fetchNotifications();
      }
    }, 10000);

    return () => clearInterval(interval);
  }, [isOpen, canSeeDriverRequests]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const fetchUnreadCount = async () => {
    try {
      const response = await notificationsAPI.getUnreadCount();
      setUnreadCount(response.data.count);
    } catch (error) {
      console.error('Error fetching unread count:', error);
    }
  };

  const fetchDriverRequestsCount = async () => {
    try {
      const response = await bookingsAPI.getDriverRequests();
      const count = response.data?.length || 0;
      setDriverRequestsCount(count);
      console.log('Driver requests count:', count);
    } catch (error) {
      console.error('Error fetching driver requests count:', error);
    }
  };

  const fetchNotifications = async () => {
    try {
      setLoading(true);
      const response = await notificationsAPI.getNotifications();
      // Limit to 20 most recent notifications
      const allNotifications = response.data || [];
      const limitedNotifications = allNotifications.slice(0, 20);
      
      // Debug: Log first notification to see structure
      if (limitedNotifications.length > 0) {
        console.log('Sample notification:', limitedNotifications[0]);
        console.log('Booking ID type:', typeof limitedNotifications[0].bookingId);
        console.log('Booking ID value:', limitedNotifications[0].bookingId);
      }
      
      setNotifications(limitedNotifications);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpen = () => {
    setIsOpen(!isOpen);
    if (!isOpen) {
      fetchNotifications();
    }
  };

  const handleNotificationClick = async (notification: Notification) => {
    // Mark as read
    if (!notification.isRead) {
      try {
        await notificationsAPI.markAsRead(notification._id);
        fetchUnreadCount();
        fetchNotifications();
      } catch (error) {
        console.error('Error marking notification as read:', error);
      }
    }

    // Check if it's a complaint notification
    const isComplaintNotification = notification.title.toLowerCase().includes('complaint') || 
                                     notification.message.toLowerCase().includes('complaint') ||
                                     notification.title.toLowerCase().includes('urgent request');
    
    // If admin and complaint notification, navigate to complaints page with highlight
    if (isComplaintNotification && user?.role === 'admin') {
      setIsOpen(false);
      // Try to extract complaint ID from notification data or navigate with a flag to show latest
      navigate('/admin/complaints?highlight=latest');
      return;
    }

    // Navigate to appropriate page based on user role and notification type
    if (notification.bookingId) {
      const bookingId = typeof notification.bookingId === 'object' ? notification.bookingId._id : notification.bookingId;
      
      // Check notification type for better routing
      const titleLower = notification.title.toLowerCase();
      const messageLower = notification.message.toLowerCase();
      
      const isReminder = titleLower.includes('reminder') || 
                         messageLower.includes('reminder') ||
                         messageLower.includes('starting in') ||
                         messageLower.includes('starting soon');
      
      const isAcceptance = titleLower.includes('accepted') ||
                          messageLower.includes('accepted') ||
                          messageLower.includes('confirmed') ||
                          notification.type === 'booking_accepted';
      
      // Determine navigation based on user role
      const isAdmin = user?.role === 'admin';
      const isProvider = user?.role === 'provider';
      const skillsArray: string[] = Array.isArray(user?.skills) ? (user?.skills || []) : 
                          (typeof user?.skills === 'string' ? [user?.skills] : []);
      const isDriver = isProvider && skillsArray.includes('driver');
      
      console.log('Notification Click Debug:', {
        title: notification.title,
        message: notification.message,
        type: notification.type,
        isReminder,
        isAcceptance,
        isAdmin,
        isProvider,
        isDriver,
        skills: user?.skills,
        bookingId
      });
      
      // Admin: Navigate to admin dashboard (shows all bookings)
      if (isAdmin) {
        console.log('Navigating admin to admin dashboard');
        navigate('/admin');
        setIsOpen(false);
        return;
      }
      
      if (isReminder && isDriver) {
        // Driver: Navigate to driver-map view for reminders
        console.log('Navigating driver to driver-map:', bookingId);
        navigate(`/driver-map/${bookingId}`);
      } else if (isReminder && !isProvider) {
        // Customer: Navigate to trip view for reminders
        console.log('Navigating customer to trip:', bookingId);
        navigate(`/trip/${bookingId}`);
      } else if (isAcceptance && !isProvider) {
        // Customer: Driver accepted, navigate to trip view
        console.log('Navigating customer to trip view for accepted booking:', bookingId);
        navigate(`/trip/${bookingId}`);
      } else if (isDriver) {
        // Driver: For other notifications, go to provider bookings (could enhance to specific booking)
        console.log('Navigating driver to provider bookings');
        navigate('/provider-bookings');
      } else {
        // Customer: For other booking notifications, go to my bookings
        console.log('Navigating customer to bookings');
        navigate('/bookings');
      }
      
      setIsOpen(false);
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await notificationsAPI.markAllAsRead();
      fetchUnreadCount();
      fetchNotifications();
    } catch (error) {
      console.error('Error marking all as read:', error);
    }
  };

  const handleClearAll = async () => {
    if (!window.confirm('Are you sure you want to clear all notifications? They will be hidden but not permanently deleted.')) {
      return;
    }
    
    try {
      const response = await notificationsAPI.clearAll();
      console.log(`✅ Cleared ${response.data?.count || 0} notifications`);
      fetchUnreadCount();
      fetchNotifications();
      setIsOpen(false);
    } catch (error) {
      console.error('Error clearing all notifications:', error);
      alert('Failed to clear notifications. Please try again.');
    }
  };

  const getIcon = (type: string) => {
    switch (type) {
      case 'success':
      case 'booking_accepted':
      case 'booking_completed':
        return <Check className="h-5 w-5 text-green-600" />;
      case 'warning':
      case 'booking_cancelled':
        return <AlertCircle className="h-5 w-5 text-yellow-600" />;
      case 'error':
      case 'booking_rejected':
        return <X className="h-5 w-5 text-red-600" />;
      default:
        return <Bell className="h-5 w-5 text-blue-600" />;
    }
  };

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    const diffDays = Math.floor(diffHours / 24);
    return `${diffDays}d ago`;
  };

  const totalNotificationCount = unreadCount + driverRequestsCount;

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={handleOpen}
        className="relative p-2 text-gray-300 hover:text-white transition-colors"
        title="Notifications"
      >
        <Bell className="h-6 w-6" />
        {totalNotificationCount > 0 && (
          <span className="absolute top-0 right-0 inline-flex items-center justify-center px-2 py-1 text-xs font-bold leading-none text-white transform translate-x-1/2 -translate-y-1/2 bg-red-600 rounded-full">
            {totalNotificationCount > 99 ? '99+' : totalNotificationCount}
          </span>
        )}
      </button>

      {isOpen && (
        <div 
          className="fixed sm:absolute left-4 right-4 sm:left-auto sm:right-0 mt-2 w-auto sm:w-96 bg-white rounded-lg shadow-xl border border-gray-200 z-50"
          style={{ 
            maxWidth: 'min(24rem, calc(100vw - 1rem))',
            width: 'auto'
          }}
        >
          {/* Header */}
                <div className="px-3 sm:px-4 py-2 sm:py-3 border-b border-gray-200 flex items-center justify-between">
          <h3 className="text-base sm:text-lg font-semibold text-gray-900">{t('notifications.title')}</h3>
          <div className="flex items-center space-x-2">
            {unreadCount > 0 && (
              <button
                onClick={handleMarkAllRead}
                className="text-xs sm:text-sm text-black600 hover:text-black700 font-medium whitespace-nowrap"
              >
                {t('notifications.markAllRead')}
              </button>
            )}
            {notifications.length > 0 && (
              <button
                onClick={handleClearAll}
                className="text-xs sm:text-sm text-red-600 hover:text-red-700 font-medium whitespace-nowrap"
              >
                {t('notifications.clearAll')}
              </button>
            )}
          </div>
        </div>

          {/* Driver Requests Section (for drivers and admins) */}
          {canSeeDriverRequests && driverRequestsCount > 0 && (
            <div className="border-b border-gray-200">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  console.log('Driver requests button clicked');
                  setIsOpen(false);
                  setTimeout(() => {
                    console.log('Opening driver requests modal');
                    setIsDriverRequestsModalOpen(true);
                  }, 100);
                }}
                className="w-full px-3 sm:px-4 py-2 sm:py-3 hover:bg-white transition-colors flex items-center justify-between"
              >
                <div className="flex items-center space-x-2 sm:space-x-3 min-w-0 flex-1">
                  <div className="flex-shrink-0 w-8 h-8 sm:w-10 sm:h-10 bg-black100 rounded-full flex items-center justify-center">
                    <Car className="h-4 w-4 sm:h-5 sm:w-5 text-black600" />
                  </div>
                  <div className="text-left min-w-0 flex-1">
                    <p className="text-xs sm:text-sm font-semibold text-gray-900 truncate">
                      {isAdmin ? t('notifications.newDriverRequestsAdmin') : t('notifications.newDriverRequests')}
                    </p>
                    <p className="text-xs text-gray-600 truncate">
                      {driverRequestsCount} {driverRequestsCount === 1 ? t('notifications.request') : t('notifications.requests')} {t('notifications.pending')}
                    </p>
                  </div>
                </div>
                <span className="px-2 py-1 bg-black text-white text-xs font-bold rounded-full flex-shrink-0 ml-2">
                  {driverRequestsCount}
                </span>
              </button>
            </div>
          )}

          {/* Notifications List */}
          <div className="max-h-[60vh] sm:max-h-96 overflow-y-auto">
            {loading ? (
              <div className="p-6 sm:p-8 text-center">
                <div className="animate-spin rounded-full h-6 w-6 sm:h-8 sm:w-8 border-b-2 border-black mx-auto"></div>
              </div>
            ) : notifications.length === 0 ? (
              <div className="p-6 sm:p-8 text-center text-gray-500">
                <Bell className="h-10 w-10 sm:h-12 sm:w-12 mx-auto mb-2 text-gray-300" />
                <p className="text-xs sm:text-sm">{t('notifications.noNotifications')}</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-100">
                {notifications.map((notification) => (
                  <button
                    key={notification._id}
                    onClick={() => handleNotificationClick(notification)}
                    className={`w-full px-3 sm:px-4 py-2 sm:py-3 hover:bg-gray-50 transition-colors text-left ${
                      !notification.isRead ? 'bg-blue-50' : ''
                    }`}
                  >
                    <div className="flex items-start space-x-2 sm:space-x-3">
                      <div className="flex-shrink-0 mt-1">
                        {getIcon(notification.type)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs sm:text-sm font-medium text-gray-900 line-clamp-1 sm:truncate">
                          {notification.title}
                        </p>
                        <p className="text-xs sm:text-sm text-gray-600 line-clamp-2 mt-1">
                          {notification.message}
                        </p>
                        {(() => {
                          if (!notification.bookingId) return null;
                          
                          let displayText = null;
                          
                          // If bookingId is populated (object), extract the bookingId field
                          if (typeof notification.bookingId === 'object' && notification.bookingId !== null) {
                            const bookingIdValue = notification.bookingId.bookingId;
                            if (bookingIdValue) {
                              displayText = `${t('notifications.bookingId')}: ${bookingIdValue}`;
                            }
                          }
                          // If it's a number, display directly
                          else if (typeof notification.bookingId === 'number') {
                            displayText = `${t('notifications.bookingId')}: ${notification.bookingId}`;
                          }
                          // If it's a string (MongoDB _id or DriverRequest ID), show friendly message
                          else if (typeof notification.bookingId === 'string' && notification.bookingId.length > 0) {
                            // Check if title contains "Driver Request" - these are pre-booking notifications
                            if (notification.title.includes('Driver Request') || notification.title.includes('New Driver') || notification.title.includes('Request Expired')) {
                              displayText = `${t('notifications.requestId')}: ${notification.bookingId.slice(-6).toUpperCase()}`;
                            } else {
                              displayText = `${t('notifications.ref')}: ${notification.bookingId.slice(-8).toUpperCase()}`;
                            }
                          }
                          
                          if (!displayText) return null;
                          
                          return (
                            <p className="text-xs text-black600 font-medium mt-1">
                              {displayText}
                            </p>
                          );
                        })()}
                        <div className="flex items-center mt-1 sm:mt-2">
                          <Clock className="h-3 w-3 text-gray-400 mr-1 flex-shrink-0" />
                          <span className="text-xs text-gray-400 truncate">
                            {formatTime(notification.createdAt)}
                          </span>
                          {!notification.isRead && (
                            <span className="ml-2 w-2 h-2 bg-blue-600 rounded-full flex-shrink-0"></span>
                          )}
                        </div>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          {notifications.length > 0 && (
            <div className="px-3 sm:px-4 py-2 sm:py-3 border-t border-gray-200">
              <p className="w-full text-center text-xs text-gray-500">
                {t('notifications.showingRecent')}
              </p>
            </div>
          )}
        </div>
      )}

      {/* Driver Requests Modal */}
      <DriverRequestsModal 
        isOpen={isDriverRequestsModalOpen} 
        onClose={() => {
          setIsDriverRequestsModalOpen(false);
          fetchDriverRequestsCount(); // Refresh count after closing
        }} 
      />
    </div>
  );
};

export default GeneralNotificationBell;

